import React, { useMemo } from 'react';

type Doc = {
  title: string;
  version: string;
  effectiveAt: string | null;
  sha256: string;
  bodyMarkdown: string;
};

function isHeading(line: string): { level: number; text: string } | null {
  const m = line.match(/^(#{1,3})\s+(.+)\s*$/);
  if (!m) return null;
  return { level: m[1].length, text: m[2] };
}

function parseInline(text: string): React.ReactNode[] {
  // Minimal inline link support: [label](url)
  const out: React.ReactNode[] = [];
  let rest = text;
  while (rest.length) {
    const idx = rest.indexOf('[');
    if (idx === -1) {
      out.push(rest);
      break;
    }
    if (idx > 0) out.push(rest.slice(0, idx));
    const endLabel = rest.indexOf(']', idx + 1);
    const startUrl = endLabel !== -1 ? rest.indexOf('(', endLabel + 1) : -1;
    const endUrl = startUrl !== -1 ? rest.indexOf(')', startUrl + 1) : -1;
    if (endLabel === -1 || startUrl === -1 || endUrl === -1) {
      out.push(rest.slice(idx));
      break;
    }
    const label = rest.slice(idx + 1, endLabel);
    const url = rest.slice(startUrl + 1, endUrl);
    const safeHref = (() => {
      const trimmed = url.trim();
      if (!trimmed) return null;
      // Allow same-site relative links.
      if (trimmed.startsWith('/')) return trimmed;
      // Allow mailto links for contact addresses.
      if (trimmed.toLowerCase().startsWith('mailto:')) return trimmed;
      // Allow http(s) only.
      try {
        const parsed = new URL(trimmed);
        const protocol = parsed.protocol.toLowerCase();
        if (protocol === 'https:' || protocol === 'http:') return trimmed;
      } catch {
        // Not a valid absolute URL; treat as unsafe.
      }
      return null;
    })();
    out.push(
      safeHref ? (
        <a key={`${label}-${url}-${out.length}`} href={safeHref} target="_blank" rel="noreferrer">
          {label}
        </a>
      ) : (
        <span key={`${label}-${url}-${out.length}`}>{label}</span>
      ),
    );
    rest = rest.slice(endUrl + 1);
  }
  return out;
}

export function LegalDocumentView({ doc }: { doc: Doc }) {
  const lines = useMemo(() => doc.bodyMarkdown.split('\n'), [doc.bodyMarkdown]);

  const toc = useMemo(() => {
    const items: { id: string; text: string }[] = [];
    for (const line of lines) {
      const h = isHeading(line);
      if (h && h.level === 2) {
        const id = h.text
          .toLowerCase()
          .replaceAll(/[^a-z0-9]+/g, '-')
          .replaceAll(/(^-|-$)/g, '');
        items.push({ id, text: h.text });
      }
    }
    return items;
  }, [lines]);

  return (
    <div className="panel" style={{ maxWidth: 980, margin: '0 auto' }}>
      <h1 style={{ marginBottom: 10 }}>{doc.title}</h1>
      <p className="muted" style={{ marginTop: 0 }}>
        Version <strong>{doc.version}</strong>
        {doc.effectiveAt ? (
          <>
            {' '}
            · Effective <strong>{new Date(doc.effectiveAt).toLocaleDateString()}</strong>
          </>
        ) : null}
      </p>

      {toc.length ? (
        <div className="card" style={{ padding: 14, marginBottom: 18 }}>
          <strong>Contents</strong>
          <ul style={{ marginTop: 10, marginBottom: 0 }}>
            {toc.map((item) => (
              <li key={item.id}>
                <a href={`#${item.id}`}>{item.text}</a>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      <div style={{ lineHeight: 1.6 }}>
        {lines.map((raw, idx) => {
          const line = raw.trimEnd();
          const heading = isHeading(line);
          if (heading) {
            const id =
              heading.level === 2
                ? heading.text
                    .toLowerCase()
                    .replaceAll(/[^a-z0-9]+/g, '-')
                    .replaceAll(/(^-|-$)/g, '')
                : undefined;
            if (heading.level === 1) return <h2 key={idx}>{heading.text}</h2>;
            if (heading.level === 2)
              return (
                <h3 key={idx} id={id}>
                  {heading.text}
                </h3>
              );
            return <h4 key={idx}>{heading.text}</h4>;
          }
          if (!line) return <div key={idx} style={{ height: 10 }} />;
          if (line.startsWith('- ')) {
            // Let the UL renderer handle lists by returning null here.
            return null;
          }
          if (line.startsWith('>')) {
            return (
              <blockquote key={idx} style={{ borderLeft: '3px solid #c9d4e3', paddingLeft: 12, marginLeft: 0 }}>
                {parseInline(line.replace(/^>\s?/, ''))}
              </blockquote>
            );
          }
          if (line.startsWith('**') && line.endsWith('**')) {
            return (
              <p key={idx}>
                <strong>{line.slice(2, -2)}</strong>
              </p>
            );
          }
          return <p key={idx}>{parseInline(line)}</p>;
        })}
      </div>

      <p className="muted" style={{ marginTop: 22, fontSize: 12 }}>
        Document hash (SHA-256): <code>{doc.sha256}</code>
      </p>
    </div>
  );
}
